import { useMutation, UseMutationOptions } from "@tanstack/react-query";

import { authClient } from "lib/auth/authClient";
import { awsWafTokenHeaders } from "lib/auth/awsWafToken";
import { SKIP_SENTRY_FOR_CLIENT_ERRORS } from "lib/sentryErrors";
import { BetterAuthError, BetterAuthUserData } from "./utils";

type SignInVariables = {
  email: string;
  password: string;
};

// better-auth's client types for `signIn.email` collapse to `any` under our plugin setup, so we
// type the resolved `{ data, error }` explicitly instead of deriving an unreliable type. The data
// shape mirrors better-auth's sign-in route types (dist/api/routes/sign-in) and the two-factor plugin
// (`twoFactorRedirect`). `token`/`user` are present on a completed sign-in but absent on the
// 2FA-challenge response, so they're optional. Company-mandated 2FA setup isn't signalled here; it's
// on the session (`twoFactorSetupRequired`, see betterAuthSessionQuery).
type SignInResponseData = {
  redirect?: boolean;
  token?: string;
  url?: string;
  twoFactorRedirect?: boolean;
  user?: BetterAuthUserData;
};

type SignInResponse =
  | { data: SignInResponseData; error: null }
  | { data: null; error: BetterAuthError };

// Thin React Query wrapper around better-auth's email/password sign-in so the new flow inherits the
// app-wide mutation error handling / Sentry reporting configured in AppProviders. better-auth
// resolves with `{ data, error }` rather than throwing, so we re-throw the error object directly
// to drive useMutation's onError / isError states — preserving `code` and `status` for callers.
export function useSignIn(
  options?: UseMutationOptions<SignInResponseData, BetterAuthError, SignInVariables, unknown>,
) {
  return useMutation({
    meta: SKIP_SENTRY_FOR_CLIENT_ERRORS,
    mutationKey: ["signIn"],
    mutationFn: async ({ email, password }: SignInVariables): Promise<SignInResponseData> => {
      // Anti-bot: attach a WAF token to this request (see awsWafToken.ts).
      const wafHeaders = await awsWafTokenHeaders();
      const { data, error }: SignInResponse = await authClient.signIn.email(
        { email, password },
        { headers: wafHeaders },
      );
      if (error) {
        throw error;
      }
      return data;
    },
    ...options,
  });
}
